import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { socApi, type SocPolicy } from '@/api/soc'
import { sourceLabel } from '@/pages/notificacoes/preferencias-constants'

const errMsg = (e: unknown, fallback: string) =>
  (e as any)?.response?.data?.detail || (e as Error)?.message || fallback

/** "Wazuh (SIEM) e Zabbix" — lista em pt-BR, com "e" antes do último. */
function listaEmPt(itens: string[]): string {
  if (itens.length <= 1) return itens[0] ?? ''
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`
}

export function useSocTeams() {
  return useQuery({
    queryKey: ['soc-teams'],
    queryFn: () => socApi.listarTimes(),
    staleTime: 60_000,
  })
}

export function useSocTenants(orgId: number | null) {
  return useQuery({
    queryKey: ['soc-tenants', orgId],
    queryFn: () => socApi.tenantsDaOrg(orgId as number),
    enabled: orgId != null,
    staleTime: 60_000,
  })
}

export function useSocCatalogo(teamId: number | null) {
  return useQuery({
    // `teamId` DENTRO da queryKey: sem ele o cache do react-query serviria o
    // catálogo de um cliente para outro ao trocar de time — defeito silencioso,
    // só visível com dois clientes na mesma sessão (Fase 2 / Task 2.3).
    queryKey: ['soc-catalogo', teamId],
    queryFn: () => socApi.catalogo(teamId as number),
    enabled: teamId != null,
    staleTime: 5 * 60_000,
  })
}

export function useSocPoliticas(teamId: number | null) {
  return useQuery({
    queryKey: ['soc-politicas', teamId],
    queryFn: () => socApi.politicas(teamId as number),
    enabled: teamId != null,
  })
}

export function useSocNaoClassificados(teamId: number | null) {
  return useQuery({
    queryKey: ['soc-nao-classificados', teamId],
    queryFn: () => socApi.naoClassificados(teamId as number),
    enabled: teamId != null,
    // Curto de propósito: o balde é o que muda sozinho, sem ninguém salvar nada.
    staleTime: 30_000,
  })
}

export function useSocCandidatos(teamId: number | null) {
  return useQuery({
    queryKey: ['soc-candidatos', teamId],
    queryFn: () => socApi.candidatos(teamId as number),
    enabled: teamId != null,
  })
}

export function useCriarSocTeam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: socApi.criarTime,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['soc-tenants'] })
      // A aba "Times de SOC" (AbaTimesDeSoc, useSocTeams) le da MESMA chave.
      // Sem invalidar aqui, staleTime de 60s deixa a aba dizer "sem time"
      // logo depois de um criado — a mesma classe de defeito que o achado
      // Important 3 da revisao final apontou (leitor com dado velho na
      // exata afirmacao que a tela existe para fazer).
      qc.invalidateQueries({ queryKey: ['soc-teams'] })
      notifications.show({ message: `Time "${data.name}" criado.`, color: 'green' })
    },
    onError: (e) =>
      notifications.show({
        message: errMsg(e, 'Falha ao criar o time de SOC.'),
        color: 'red',
      }),
  })
}

export function useDefinirMembros(teamId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (levels: Record<string, number[]>) =>
      socApi.definirMembros(teamId as number, levels),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['soc-tenants'] })
      // Mesma razao do `useCriarSocTeam` acima: `members_by_level` da aba
      // "Times de SOC" vem desta chave, e sem invalidar ela pode dizer
      // "Nivel 1: ninguem" logo apos os niveis serem preenchidos.
      qc.invalidateQueries({ queryKey: ['soc-teams'] })
      // Mesmo raciocinio, mas para quem preenche `EditorDeIntegrantes`: e esta
      // chave (`nivel_atual` de cada candidato) que semeia a selecao inicial do
      // editor. Sem invalidar aqui, "Voltar para a lista" e reabrir o MESMO
      // time (sem F5) reidrata o editor com o cache velho — que, na primeira
      // vez que o time e povoado, e "ninguem em nivel nenhum" — trava no guarda
      // `carregadoDe` do editor, e o proximo "Salvar" apaga quem acabou de
      // entrar. So um F5 completo (QueryClient novo) escondia isso.
      qc.invalidateQueries({ queryKey: ['soc-candidatos', teamId] })
      if (data.sem_opt_in.length > 0) {
        // AVISO e não erro: gravou. Mas quem está aqui não recebe o alarme das
        // fontes que o time assinou, e sem este aviso o time inteiro pode ficar
        // mudo sem ninguém saber.
        //
        // O texto NOMEIA a fonte e o lugar de resolver. Até 01/09/2026 ele
        // dizia "sem notificação autorizada no aparelho", e apontava para a
        // coisa errada: o que falta é a inscrição por fonte
        // (`user_notification_subscriptions`), não a permissão do navegador
        // (`web_push_subscriptions`). Quem lia ia conferir a permissão,
        // encontrava tudo certo, e não tinha para onde ir (TASK-10284).
        const fontes = listaEmPt(data.fontes_sem_opt_in.map(sourceLabel))
        notifications.show({
          title: 'Integrantes salvos, com uma ressalva',
          message: fontes
            ? `Falta ligar o push de ${fontes} para: ${data.sem_opt_in.join(', ')}. Cada pessoa liga a fonte em Notificações › Preferências, na conta dela; até lá o alarme dessas fontes não chega a ela.`
            : `Falta ligar o push das fontes deste time para: ${data.sem_opt_in.join(', ')}. Cada pessoa liga a fonte em Notificações › Preferências, na conta dela; até lá o alarme não chega a ela.`,
          color: 'yellow',
          autoClose: 12000,
        })
        return
      }
      notifications.show({ message: 'Integrantes salvos.', color: 'green' })
    },
    // O 422 de membership traz o e-mail de quem falta no `detail`. Repassar o
    // texto do servidor É O PONTO: mensagem genérica devolve o operador para o
    // escuro, que é o defeito que a validação existe para evitar.
    onError: (e) =>
      notifications.show({
        message: errMsg(e, 'Falha ao salvar os integrantes.'),
        color: 'red',
        autoClose: 12000,
      }),
  })
}

export function useSalvarPoliticas(teamId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (items: SocPolicy[]) =>
      socApi.salvarPoliticas(teamId as number, items),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['soc-politicas', teamId] })
      qc.invalidateQueries({ queryKey: ['soc-nao-classificados', teamId] })
      notifications.show({
        message: `${data.policies} alerta(s) configurado(s), ${data.rules} regra(s) de envio.`,
        color: 'green',
      })
    },
    onError: (e) =>
      notifications.show({
        message: errMsg(e, 'Falha ao salvar a configuração dos alertas.'),
        color: 'red',
      }),
  })
}

/** Simulação do teto: quantas ligações ele teria evitado nos últimos 30 dias. */
export function useSimulacaoDeTeto(teamId: number | null, n: number | null) {
  return useQuery({
    queryKey: ['soc-simulacao-teto', teamId, n],
    queryFn: () => socApi.simulacaoDeTeto(teamId as number, n as number),
    enabled: teamId != null && n != null && n >= 1,
    // O histórico de 30 dias não muda a cada tecla; sem isto, digitar "12"
    // dispararia três consultas (1, 12 e o que vier no meio).
    staleTime: 60_000,
  })
}

export function useSalvarTeto(teamId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (max: number | null) => socApi.salvarTeto(teamId as number, max),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['soc-teams'] })
      qc.invalidateQueries({ queryKey: ['soc-simulacao-teto', teamId] })
      notifications.show({
        message:
          data.max_ligacoes_por_hora == null
            ? 'Teto removido: o SOC volta a ligar sem limite por hora.'
            : `Teto de ${data.max_ligacoes_por_hora} ligação(ões) por hora salvo.`,
        color: 'green',
      })
    },
    onError: (e) =>
      notifications.show({
        message: errMsg(e, 'Falha ao salvar o teto de ligações.'),
        color: 'red',
      }),
  })
}

/**
 * Os alertas do time que ainda pedem resposta.
 *
 * `refetchInterval` de 30s porque esta tela mostra um relógio correndo: com o
 * topo insistente, `next_due_at` é quando o telefone toca de novo. Lista
 * parada mentiria sobre o estado do plantão.
 */
export function useEscalonamentos(teamId: number | null) {
  return useQuery({
    queryKey: ['soc-escalonamentos', teamId],
    queryFn: () => socApi.escalonamentos(teamId as number),
    enabled: teamId != null,
    refetchInterval: 30_000,
  })
}

export function useResolverEscalonamento(teamId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (eventId: number) =>
      socApi.resolver(teamId as number, eventId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['soc-escalonamentos', teamId] })
      notifications.show({
        message: 'Alerta encerrado. O alarme para de tocar.',
        color: 'green',
      })
    },
    onError: () => {
      // 404 é o caso normal de corrida: alguém resolveu pelo telefone entre a
      // tela carregar e o clique. Dizer "já foi resolvido" é a informação útil;
      // "erro" faria a pessoa tentar de novo sem motivo.
      notifications.show({
        message: 'Este alerta já não estava aberto (resolvido por outra via?).',
        color: 'yellow',
      })
      qc.invalidateQueries({ queryKey: ['soc-escalonamentos', teamId] })
    },
  })
}
